import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Card, Spin, Alert, Typography } from 'antd'
import { Poll } from '../../features/poll/components/Poll'
import PollSetupProgress from '../../features/poll/components/PollSetupProgress'
import { usePoll } from '../../features/poll/hooks/usePoll'
import { buildPollNameVariables } from '../../features/poll/interpolate'
import { clearPollGuardCache } from '../../components/poll-guard'
import { PollAnswerItem, PollSetupStage } from '../../features/poll/types'
import { buildClientSetupStages } from '../../features/poll/setup-stages'
import { getLoadSessionAction } from '../../store/session/action-creators'
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch'

const { Title } = Typography

const OnboardingQuizPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const sessionUserName = useSelector(
    (state: any) =>
      state?.session?.sessionData?.user?.name || state?.session?.sessionData?.user?.login || '',
  )
  const {
    poll,
    access,
    submission,
    setup,
    loading,
    submitting,
    error,
    loadPoll,
    checkSubmission,
    submit,
    retrySetup,
  } = usePoll()
  const [phase, setPhase] = useState<'questions' | 'setup'>('questions')
  const [localStages, setLocalStages] = useState<PollSetupStage[]>([])
  const [editingAnswers, setEditingAnswers] = useState(false)

  /** Имя с формы регистрации, иначе ФИО пользователя (логин-email именем не считаем) */
  const welcomeName = useMemo(() => {
    const fromRegistration = String(access?.registration?.contact_name || '').trim()
    if (fromRegistration) return fromRegistration
    const fromSession = String(sessionUserName || '').trim()
    return fromSession && !fromSession.includes('@') ? fromSession : ''
  }, [access, sessionUserName])
  const pollVariables = useMemo(
    () => buildPollNameVariables(welcomeName || sessionUserName),
    [welcomeName, sessionUserName],
  )
  /** «Наименование бренда» по умолчанию — название организации из регистрации (редактируемо) */
  const pollPrefill = useMemo(() => {
    const company = String(access?.registration?.company_name || '').trim()
    return company ? { brand_name: company } : undefined
  }, [access])

  useEffect(() => {
    loadPoll()
    checkSubmission()
  }, [loadPoll, checkSubmission])

  useEffect(() => {
    if (access && access.needs_poll === false) {
      navigate('/', { replace: true })
    }
  }, [access, navigate])

  useEffect(() => {
    if (editingAnswers) return
    if (
      setup &&
      (setup.status === 'running' || setup.status === 'error' || setup.status === 'pending')
    ) {
      setPhase('setup')
      if (setup.stages?.length) setLocalStages(setup.stages)
    }
  }, [setup, editingAnswers])

  const finishOnboarding = async () => {
    clearPollGuardCache()
    const sessionAction: any = await dispatch(getLoadSessionAction())
    if (sessionAction?.payload?.data?.needsPoll) {
      return
    }
    navigate('/', { replace: true })
  }

  const handleSubmit = async (answers: PollAnswerItem[]) => {
    setEditingAnswers(false)
    if (poll) setLocalStages(buildClientSetupStages(poll, answers))
    setPhase('setup')
    const result = await submit(answers)
    if (result?.setup?.status === 'done') {
      await finishOnboarding()
    }
  }

  const handleRetry = async () => {
    setEditingAnswers(false)
    const result = await retrySetup()
    if (result?.status === 'done') {
      await finishOnboarding()
    }
  }

  const handleEditAnswers = () => {
    setEditingAnswers(true)
    setPhase('questions')
  }

  if (loading || access === null) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (error && phase !== 'setup') {
    return (
      <div style={{ maxWidth: 600, margin: '40px auto', padding: 24 }}>
        <Alert message="Ошибка" description={error} type="error" />
      </div>
    )
  }

  if (!poll) {
    return (
      <div style={{ maxWidth: 600, margin: '40px auto', padding: 24 }}>
        <Alert
          message="Опрос не настроен"
          description="Активный опрос не найден. Обратитесь в поддержку."
          type="info"
        />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: 24 }}>
      <Card>
        {phase === 'setup' ? (
          <PollSetupProgress
            setup={setup}
            stages={localStages}
            onRetry={handleRetry}
            onEditAnswers={handleEditAnswers}
            retrying={submitting}
          />
        ) : (
          <>
            <Title level={2} style={{ marginBottom: 24 }}>
              {welcomeName
                ? `${welcomeName}, добро пожаловать в Axioma ERP!`
                : 'Добро пожаловать в Axioma ERP!'}
            </Title>
            <Poll
              key={`poll-edit-${submission?.id || 'new'}-${editingAnswers ? 'edit' : 'view'}`}
              quiz={poll}
              initialAnswers={submission?.answers || []}
              onSubmit={handleSubmit}
              loading={submitting}
              submitText="Продолжить"
              variables={pollVariables}
              prefill={pollPrefill}
            />
          </>
        )}
      </Card>
    </div>
  )
}

export default OnboardingQuizPage
